import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import DeviceData from 'drivers/deviceData';
import Route from 'enum/route.enum';
import BackCursor from 'components/common/nav/backCursor';
import { Container, Header } from 'components/styles/Common.styled';
import { createDevicePlayer } from 'components/channels/players/devicePlayerFactory';
import { clearHttpChannelLoop } from 'components/channels/common/channelStreamUrl';
import { updatePlayerState } from 'components/channels/common/channelPlayerState';
import { translate as t } from 'utils/i18n';
import { isHdmiDefaultPreviewEnabled } from 'utils/hdmi/hdmiEnv';
import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';

async function stopActivePlayback() {
  try {
    var player = createDevicePlayer();
    if (player && typeof player.stop === 'function') {
      await player.stop();
    }
    clearHttpChannelLoop(updatePlayerState);
  } catch (error) {
    console.log('[HdmiInput] stop playback: ' + (error.message || error));
  }
}

function HdmiInput() {
  var params = useParams();
  var location = useLocation();
  var returnTo = (location.state && location.state.returnTo) || Route.route.LANDING;
  var sourceType = params.type ? String(params.type).toUpperCase() : 'HDMI';
  var sourceNumber = params.number != null ? parseInt(params.number, 10) : parseInt(params.port, 10);
  var [redirectBack, setRedirectBack] = useState(false);
  var [loadError, setLoadError] = useState(null);
  var hdmiActiveRef = useRef(false);
  var isBrowserPreview =
    isHdmiDefaultPreviewEnabled() &&
    !!(Storages.MainStorage.getItem(StorageKeys.Keys.DEVICELIST) || {}).isDEFAULT;

  var exitHdmi = useCallback(async function () {
    if (hdmiActiveRef.current) {
      await DeviceData.hideHdmiInput();
      hdmiActiveRef.current = false;
    }
    setRedirectBack(true);
  }, []);

  useEffect(function () {
    var mounted = true;

    async function onKeyPress(event) {
      var tvKeyControls = await DeviceData.getTVControl();
      if (event.keyCode === tvKeyControls.KEY_BACK) {
        event.preventDefault();
        await exitHdmi();
      }
    }

    window.addEventListener('keydown', onKeyPress);

    async function start() {
      if (!sourceNumber || isNaN(sourceNumber)) {
        if (mounted) {
          setLoadError('invalid');
        }
        return;
      }

      try {
        await stopActivePlayback();
        await DeviceData.showVideoSource(sourceType, sourceNumber);
        if (mounted) {
          hdmiActiveRef.current = true;
        }
      } catch (error) {
        console.log('[HdmiInput] show failed: ' + (error.message || error));
        if (mounted) {
          setLoadError(error.message || 'failed');
        }
      }
    }

    start();

    return function () {
      mounted = false;
      window.removeEventListener('keydown', onKeyPress);
      if (hdmiActiveRef.current) {
        hdmiActiveRef.current = false;
        DeviceData.hideHdmiInput();
      }
    };
  }, [sourceType, sourceNumber, exitHdmi]);

  useEffect(
    function () {
      if (loadError) {
        var timer = setTimeout(function () {
          setRedirectBack(true);
        }, 1500);
        return function () {
          clearTimeout(timer);
        };
      }
    },
    [loadError]
  );

  if (redirectBack) {
    return <Navigate to={returnTo} />;
  }

  return (
    <div>
      <Header />
      <BackCursor />
      <Container>
        {loadError
          ? t('Source.unavailable')
          : isBrowserPreview
            ? t('Source.preview_hint')
            : t('Source.active_hint')}
      </Container>
    </div>
  );
}

export default HdmiInput;
